import { useState, useEffect } from 'react';
import { Building, Users, Award, MessageCircle, Shield, Zap, Heart } from 'lucide-react';
import { useSiteConfig, landingStatCells } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';

// 与本页「从2021年成立至今」同一年份；角标按当年自增，写死会在跨年后失真
const FOUNDING_YEAR = 2021;

export default function AboutPage() {
  const { siteName, logoUrl, landingStats } = useSiteConfig();
  const statCells = landingStatCells(landingStats);
  const [isScrolled, setIsScrolled] = useState(false);

  // 监听滚动事件，控制导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 顶部导航 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-10 h-10"
            />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/download" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              下载 App
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-teal-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a href="/contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              联系我们
            </a>
            <a 
              href="/login" 
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* Hero 顶部区域 */}
      <section className="pt-[140px] pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-teal-800">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          {/* 标题 */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            关于我们
          </h1>

          {/* 描述文字 */}
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 leading-relaxed mb-8">
            我们相信，好的工具能提升高效的工作效率。从2021年成立至今，我们在开展工作时总结积累经验；并为活动执行者、自由职业人员、中小企业提供简单、可靠、好用的订单与预约管理解决方案。
          </p>

          {/* 联系按钮 */}
          <a href="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-full transition-colors">
            <MessageCircle className="h-4 w-4" />
            <span>我们的故事</span>
          </a>
        </div>
      </section>

      {/* 数据统计 */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* 统计数据行：四格读数与落地页同源（后台「站点信息」按真实统计维护），
              未配置的格整块不渲染，四格全空则本节不留空壳。 */}
          {statCells.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {statCells.map((cell) => (
                <div key={cell.label}>
                  <div className="text-3xl font-bold text-slate-800 mb-2">{cell.value}</div>
                  <div className="text-sm text-slate-600">{cell.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 主内容区 */}
      <main className="flex-1 py-14">
        {/* 公司简介 */}
        <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
          {/* 面包屑导航 */}
          <div className="text-xs text-slate-500 mb-8">
            <a href="/" className="hover:text-teal-600 transition-colors">首页</a>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-semibold">关于我们</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-6">我们的使命</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                中国每天有成千上万个活动，执行者每天都面临订单管理、预约冲突、客户维护不及时等问题，为此耗费大量人力和时间成本。
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                我们成立{siteName}，就是为了解决这些实际存在的痛点。我们希望通过最简单易用的数字化工具，让一线经营者能把精力放在业务本身，而不是繁琐的管理工作流程。
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                我们的目标：成为自由职业者、中小企业最值得信赖的业务管理伙伴，帮助更多活动执行者实现数字化升级。
              </p>
              <a href="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-full transition-colors">
                <MessageCircle className="h-4 w-4" />
                <span>专属咨询</span>
              </a>
            </div>
            <div className="relative">
              <img 
                src="/brand/mission-team.jpg" 
                alt="团队协作" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="text-2xl font-bold text-teal-600">{new Date().getFullYear() - FOUNDING_YEAR}年</div>
                <div className="text-sm text-slate-600">行业经验</div>
              </div>
            </div>
          </div>
        </section>

        {/* 核心价值观 */}
        <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">我们的价值观</h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            这六条准则指引我们的每一个产品决策和服务行为。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">用户至上</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                每一个功能决策都以用户需求为出发点，持续倾听，快速迭代
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">数据安全</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                身份证号等敏感信息加密存储，订单数据按账号隔离，他人只在与自己对账相关的范围内可见
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">极致性能</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                服务长期稳定可用，操作响应及时，即使在业务高峰期也能保持一致的使用体验
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Building className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">专业服务</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                通过「联系我们」页面留言或客服邮箱提问，一般 7 个工作日内回复
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">创新驱动</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                持续吸收使用者的反馈与思路，逐版迭代功能、打磨操作体验
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">诚信透明</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                公开透明的定价策略，无隐藏费用，建立长期信任关系
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* 底部导航 */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* 左侧：Logo和品牌信息 */}
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto"
              />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>

            {/* 右侧：导航链接 */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/download" className="hover:text-white transition-colors">下载 App</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>

          {/* 底部备案信息 */}
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>
    </div>
  );
}
